//Built-in libraries

import { InformationCircleIcon, PencilIcon, PlusIcon, TrashIcon } from '@heroicons/react/24/outline';
import { useState } from 'react';
//Third party libraries
import { v4 as uuidv4 } from 'uuid';
//Custom components
import Button from '@/components/UI/Button';
import Confirm from '@/components/UI/Modal/Confirm';
//Services
import { useApplicationFormContext } from '@/features/ApplicationForm/ApplicationFormProvider';
import { createInputChangedHandler } from '@/features/ApplicationForm/utils/formHandlers';
import { isObjectEmpty } from '@/utils';
import AddDevelopmentTeamForm from './AddDevelopmentTeamForm';
import { initialValues } from './constants';
import type { IDevelopmentTeam } from './types';

//STP Libraries

//Assets and Miscellaneous

//Component Begin

export default function DevelopmentTeam() {
  const { setCurrentStep, updateData, data } = useApplicationFormContext();
  const [showRemoveConfirm, setShowRemoveConfirm] = useState<boolean>(false);
  const [showAddModal, setShowAddModal] = useState<boolean>(false);
  const [developmentTeam, setDevelopmentTeam] = useState(initialValues);
  const [selectedTeam, setSelectedTeam] = useState<IDevelopmentTeam>();
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [editTeamId, setEditTeamId] = useState<string>('');

  const removeTeamHandler = (team: IDevelopmentTeam) => {
    setShowRemoveConfirm(true);
    setSelectedTeam(team);
  };

  const confirmRemoveTeam = async () => {
    setShowRemoveConfirm(false);
    const filteredTeams = data.developmentTeam.filter((t: any) => t.id !== selectedTeam?.id);
    updateData({
      developmentTeam: filteredTeams,
    });
  };

  const cancelRemoveTeam = () => {
    setShowRemoveConfirm(false);
  };

  const toggleAddModal = () => {
    resetForm();
    setShowAddModal((prev) => !prev);
  };

  const resetForm = () => {
    setDevelopmentTeam(initialValues);
    setEditTeamId('');
  };

  const inputChangedHandler = createInputChangedHandler(setDevelopmentTeam, developmentTeam);

  const validateForm = () => {
    const formData = { ...developmentTeam };

    const formDataErrors = Object.keys(formData).reduce((prev: { [key: string]: string }, next: string) => {
      if (formData[next].error.length >= 1) {
        return {
          ...prev,
          [next]: formData[next].error,
        };
      }
      if (!formData[next].isTouched && formData[next].rules.required) {
        setDevelopmentTeam((prevState) => ({
          ...prevState,
          [next]: { ...prevState[next], error: 'Required' },
        }));
        return {
          ...prev,
          [next]: 'Required',
        };
      }
      return { ...prev };
    }, {});

    return isObjectEmpty(formDataErrors);
  };

  const onAddTeamHandler = () => {
    if (validateForm()) {
      const formattedData = Object.keys(developmentTeam).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return { ...prev, [next]: developmentTeam[next].value };
        },
        {}
      );
      updateData({
        developmentTeam: [...data.developmentTeam, { ...formattedData, id: uuidv4() }],
      });
      resetForm();
      toggleAddModal();
    }
  };

  const editTeamHandler = (team: any) => {
    toggleAddModal();
    setIsEditing(true);
    const formData = Object.keys(developmentTeam).reduce((prev, next) => {
      return {
        ...prev,
        [next]: {
          ...developmentTeam[next],
          value: team[next],
          isTouched: !!team[next],
        },
      };
    }, {});
    setDevelopmentTeam(formData);
    setEditTeamId(team.id);
  };

  const updateTeamHandler = () => {
    toggleAddModal();
    if (validateForm()) {
      const formattedData = Object.keys(developmentTeam).reduce(
        (
          prev: {
            [key: string]: string | number | boolean | Object | undefined;
          },
          next: string
        ) => {
          return {
            ...prev,
            [next]: developmentTeam[next].value,
            id: editTeamId,
          };
        },
        {}
      );
      updateData({
        developmentTeam: data.developmentTeam.map((t: any) => {
          if (t.id === editTeamId) {
            return formattedData;
          }
          return t;
        }),
      });
      resetForm();
      setIsEditing(false);
    }
  };

  return (
    <div className='space-y-6 p-6 lg:col-span-8 rounded-lg'>
      <h3 className='text-lg leading-6 font-medium text-primaryColor-normal'>Development Team</h3>
      <div className='flex flex-row justify-between'>
        <p className='block text-sm text-neutralColor-dark'>Please provide the name of the current team member.</p>
        <Button label='Add' type='button' btnStyle='primary' onClick={toggleAddModal} icon={<PlusIcon />} />
      </div>
      {showRemoveConfirm && (
        <Confirm
          title='Remove Development Team'
          message={`You are about to remove ${selectedTeam?.companyName} development team. Are you sure you want to remove ?`}
          acceptButtonLabel='Remove'
          declineButtonLabel='Cancel'
          accept={confirmRemoveTeam}
          decline={cancelRemoveTeam}
        />
      )}
      {showAddModal && (
        <AddDevelopmentTeamForm
          isEditing={isEditing}
          team={developmentTeam}
          modalHandler={toggleAddModal}
          inputChangedHandler={inputChangedHandler}
          onAddTeamHandler={onAddTeamHandler}
          updateTeamHandler={updateTeamHandler}
        />
      )}
      {data.developmentTeam.length === 0 && (
        <div className='p-2 mt-2 flex flex-col items-center justify-center gap-2 h-screen/2'>
          <span>
            <InformationCircleIcon className='h-8 w-8 text-tertiaryColor-normal' />
          </span>
          <p>You have not added any current team members</p>
        </div>
      )}
      {data.developmentTeam && data.developmentTeam.length > 0 && (
        <div className='space-y-2 w-full h-full ring-1 ring-neutralColor-lighter rounded-xl overflow-hidden'>
          <table className='divide-y divide-gray-200 w-full'>
            <thead className='bg-gray-50'>
              <tr>
                <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>Role</th>
                <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'>Company Name</th>
                <th className='p-4 text-left text-xs font-bold text-gray-500 capitalize tracking-wider'></th>
              </tr>
            </thead>
            <tbody className='bg-white divide-y divide-gray-200 rounded-lg'>
              {data.developmentTeam.map((team: IDevelopmentTeam) => (
                <tr
                  key={team.id}
                  className={`${team.id === editTeamId ? 'bg-neutralColor-lighter' : 'cursor-pointer hover:bg-neutralColor-lighter'}`}
                >
                  <td className='p-4'>{team.role}</td>
                  <td className='p-4'>{team.companyName}</td>
                  <td className='p-4 flex gap-4'>
                    <div className=' cursor-pointer' onClick={() => editTeamHandler(team)}>
                      <PencilIcon className='h-5 w-5 text-neutralColor-normal hover:text-blue-500' />
                    </div>
                    <div className='cursor-pointer' onClick={() => removeTeamHandler(team)}>
                      <TrashIcon className='h-5 w-5 text-neutralColor-normal hover:text-tertiaryColor-normal' />
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      <div className='flex gap-4 justify-end'>
        <Button label='Previous' btnStyle='neutral' type='button' onClick={() => setCurrentStep((prev) => prev - 1)} />
        <Button label='Next' btnStyle='primary' type='button' onClick={() => setCurrentStep((prev) => prev + 1)} />
      </div>
    </div>
  );
}
